import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DsTabNav, type DsTab } from '@/modules/internal-v2/ds';
import { PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/hooks/use-permissions';
import './admin.css';

interface AdminTab extends DsTab {
  path: string;
  /** Permission required to see and open this tab. */
  permission: string;
}

const TABS: AdminTab[] = [
  { id: 'sync', label: 'Synchronisierung', path: '/i/admin', permission: PERMISSIONS.ADMIN_VIEW },
  { id: 'supplier-feeds', label: 'Lieferantenpreislisten', path: '/i/admin/supplier-price-lists', permission: PERMISSIONS.SUPPLIER_PRICE_LISTS_VIEW },
  { id: 'specifications', label: 'Spezifikationen', path: '/i/admin/specifications', permission: PERMISSIONS.SPECIFICATIONS_VIEW },
  { id: 'users', label: 'Benutzer', path: '/i/admin/users', permission: PERMISSIONS.USERS_MANAGE },
  { id: 'roles', label: 'Rollen', path: '/i/admin/roles', permission: PERMISSIONS.ROLES_MANAGE },
  { id: 'announcements', label: 'Ankündigungen', path: '/i/admin/announcements', permission: PERMISSIONS.ANNOUNCEMENTS_MANAGE },
];

function resolveActiveTab(pathname: string): string {
  for (let i = TABS.length - 1; i >= 0; i--) {
    if (pathname === TABS[i].path || pathname.startsWith(TABS[i].path + '/')) {
      return TABS[i].id;
    }
  }
  return TABS[0].id;
}

export function AdminPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { has } = usePermissions();

  const visibleTabs = TABS.filter((tab) => has(tab.permission));
  const activeTab = resolveActiveTab(pathname);
  const activeTabDef = TABS.find((tab) => tab.id === activeTab);

  // Landed on a tab the user can't access (e.g. the /i/admin index without
  // ADMIN_VIEW) — send them to the first tab they can reach.
  if (activeTabDef && !has(activeTabDef.permission)) {
    return <Navigate to={visibleTabs[0]?.path ?? '/i'} replace />;
  }

  return (
    <div className="admin-main">
      <DsTabNav
        tabs={visibleTabs}
        activeTab={activeTab}
        onTabChange={(id) => {
          const tab = TABS.find((t) => t.id === id);
          if (tab) navigate(tab.path);
        }}
        maxWidth={1200}
      />

      <Outlet />
    </div>
  );
}
